import React, { useState } from 'react';
import { X, Send, Sparkles, User, Bot, AlertCircle, Mic, MicOff, Paperclip, Camera, Zap } from 'lucide-react';
import { useAIChat } from '../hooks/useAIChat';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
const AIAssistant = () => {
  const {
    language
  } = useLanguage();
  const {
    isOpen,
    toggleChat,
    messages,
    isLoading,
    input,
    setInput,
    sendMessage,
    messagesEndRef,
    isUsingFallback,
    isRecording,
    startVoiceRecording,
    stopVoiceRecording,
    selectedFiles,
    handleFileSelect,
    handleCameraCapture,
    removeFile,
    fileInputRef,
    cameraInputRef,
    generalInquiryCount,
    trungInquiryCount,
    generalInquiryLimit,
    trungInquiryLimit
  } = useAIChat();
  const [showApiWarning, setShowApiWarning] = useState(true);
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };
  const remainingGeneralInquiries = generalInquiryLimit - generalInquiryCount;
  const remainingTrungInquiries = trungInquiryLimit - trungInquiryCount;
  const generalInquiryPercentage = generalInquiryCount / generalInquiryLimit * 100;
  const trungInquiryPercentage = trungInquiryCount / trungInquiryLimit * 100;
  // Color based on remaining inquiries
  const getInquiryColor = (remaining: number, limit: number) => {
    const percentage = remaining / limit * 100;
    if (percentage > 60) return 'text-green-500 bg-green-50 dark:bg-green-900/20';
    if (percentage > 20) return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    return 'text-red-500 bg-red-50 dark:bg-red-900/20';
  };
  const translations = {
    en: {
      buttonText: 'Ask AI Helper',
      title: "Trung's AI Assistant",
      online: 'Online',
      placeholder: 'Ask about Trung...',
      disclaimer: 'AI can make mistakes. Please verify important details.',
      suggestions: ["Summarize Trung's experience", 'What are his top skills?', 'Why should I hire him?', 'Tell me about his projects'],
      apiWarning: 'API currently unavailable. Using pre-built responses. We apologize for the inconvenience.',
      recording: 'Recording...',
      voiceTooltip: 'Voice message',
      fileTooltip: 'Attach file',
      cameraTooltip: 'Take photo',
      generalInquiries: 'General inquiries',
      trungInquiries: 'Trung inquiries',
      remaining: 'remaining'
    },
    vi: {
      buttonText: 'Hỏi Trợ Lý AI',
      title: 'Trợ Lý AI của Trung',
      online: 'Trực tuyến',
      placeholder: 'Hỏi về Trung...',
      disclaimer: 'AI có thể mắc lỗi. Vui lòng xác minh thông tin quan trọng.',
      suggestions: ['Tóm tắt kinh nghiệm của Trung', 'Kỹ năng hàng đầu của anh ấy là gì?', 'Tại sao tôi nên tuyển anh ấy?', 'Kể về các dự án của anh ấy'],
      apiWarning: 'API hiện không khả dụng. Đang sử dụng câu trả lời có sẵn. Xin lỗi vì sự bất tiện này.',
      recording: 'Đang ghi âm...',
      voiceTooltip: 'Tin nhắn thoại',
      fileTooltip: 'Đính kèm tệp',
      cameraTooltip: 'Chụp ảnh',
      generalInquiries: 'Câu hỏi chung',
      trungInquiries: 'Câu hỏi về Trung',
      remaining: 'còn lại'
    }
  };
  const t = translations[language];
  // Check if any message is a general topic or about Trung
  const hasGeneralQuestions = messages.some(m => m.isGeneralTopic);
  const hasTrungQuestions = messages.some(m => !m.isGeneralTopic && m.role === 'user');
  return <>
      {/* Floating Button */}
      <motion.button onClick={toggleChat} className="fixed bottom-24 right-6 z-[60] flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300" whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1
    }} data-id="element-57">
        {isOpen ? <X size={24} data-id="element-58" /> : <>
            <Sparkles size={20} className="animate-pulse" data-id="element-59" />
            <span className="font-semibold hidden sm:inline" data-id="element-60">
              {t.buttonText}
            </span>
          </>}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence data-id="element-61">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="fixed bottom-40 right-4 sm:right-6 w-[90vw] sm:w-[380px] h-[500px] max-h-[70vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden z-[60] border border-gray-200 dark:border-gray-700" data-id="element-62">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 flex items-center justify-between text-white" data-id="element-63">
              <div className="flex items-center gap-2" data-id="element-64">
                <div className="bg-white/20 p-1.5 rounded-lg" data-id="element-65">
                  <Bot size={20} data-id="element-66" />
                </div>
                <div data-id="element-67">
                  <h3 className="font-bold text-sm" data-id="element-68">{t.title}</h3>
                  <p className="text-xs text-blue-100 flex items-center gap-1" data-id="element-69">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-70"></span>
                    {t.online}
                  </p>
                </div>
              </div>
              <button onClick={toggleChat} className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded transition-colors" data-id="element-71">
                <X size={18} data-id="element-72" />
              </button>
            </div>

            {/* Inquiry Counters */}
            <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-73">
              {/* General Inquiry Counter */}
              {hasGeneralQuestions && generalInquiryCount > 0 && <div className={`px-4 py-2 flex items-center justify-between ${getInquiryColor(remainingGeneralInquiries, generalInquiryLimit)}`} data-id="element-74">
                  <div className="flex items-center gap-2" data-id="element-75">
                    <Zap size={14} data-id="element-76" />
                    <span className="text-xs font-medium" data-id="element-77">
                      {t.generalInquiries}: {remainingGeneralInquiries}/
                      {generalInquiryLimit} {t.remaining}
                    </span>
                  </div>
                  <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-78">
                    <div className="h-full bg-current transition-all duration-300" style={{
                width: `${generalInquiryPercentage}%`
              }} data-id="element-79" />
                  </div>
                </div>}

              {/* Trung Inquiry Counter */}
              {hasTrungQuestions && trungInquiryCount > 0 && <div className={`px-4 py-2 flex items-center justify-between ${getInquiryColor(remainingTrungInquiries, trungInquiryLimit)}`} data-id="element-80">
                  <div className="flex items-center gap-2" data-id="element-81">
                    <Sparkles size={14} data-id="element-82" />
                    <span className="text-xs font-medium" data-id="element-83">
                      {t.trungInquiries}: {remainingTrungInquiries}/
                      {trungInquiryLimit} {t.remaining}
                    </span>
                  </div>
                  <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-84">
                    <div className="h-full bg-current transition-all duration-300" style={{
                width: `${trungInquiryPercentage}%`
              }} data-id="element-85" />
                  </div>
                </div>}
            </div>

            {/* API Warning Banner */}
            {isUsingFallback && showApiWarning && <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 p-3 flex items-start gap-2" data-id="element-86">
                <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" data-id="element-87" />
                <div className="flex-1" data-id="element-88">
                  <p className="text-xs text-amber-800 dark:text-amber-200" data-id="element-89">
                    {t.apiWarning}
                  </p>
                </div>
                <button onClick={() => setShowApiWarning(false)} className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200" data-id="element-90">
                  <X size={14} data-id="element-91" />
                </button>
              </div>}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-slate-900/50" data-id="element-92">
              {messages.map((msg, idx) => <div key={idx} className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-93">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-94">
                    {msg.role === 'user' ? <User size={14} data-id="element-95" /> : <Bot size={14} data-id="element-96" />}
                  </div>
                  <div className={`max-w-[80%] p-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 shadow-sm border border-gray-100 dark:border-gray-700 rounded-tl-none'}`} data-id="element-97">
                    {msg.content}
                    {msg.image && <img src={msg.image} alt="Uploaded" className="mt-2 rounded-lg max-w-full" data-id="element-98" />}
                  </div>
                </div>)}

              {isLoading && <div className="flex items-start gap-2" data-id="element-99">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-100">
                    <Bot size={14} data-id="element-101" />
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-102">
                    <div className="flex gap-1" data-id="element-103">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-104"></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-105"></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-106"></span>
                    </div>
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-107" />
            </div>

            {/* Suggestions (only show if few messages) */}
            {messages.length < 3 && <div className="px-4 pb-2 bg-gray-50 dark:bg-slate-900/50 overflow-x-auto flex gap-2 no-scrollbar" data-id="element-108">
                {t.suggestions.map((s, i) => <button key={i} onClick={() => sendMessage(s)} disabled={isLoading} className="whitespace-nowrap text-xs bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 px-3 py-1.5 rounded-full hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-109">
                    {s}
                  </button>)}
              </div>}

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && <div className="px-4 py-2 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-200 dark:border-gray-700" data-id="element-110">
                <div className="flex flex-wrap gap-2" data-id="element-111">
                  {selectedFiles.map((file, idx) => <div key={idx} className="flex items-center gap-2 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-lg text-xs border border-gray-200 dark:border-gray-700" data-id="element-112">
                      <Paperclip size={14} className="text-gray-500" data-id="element-113" />
                      <span className="text-gray-700 dark:text-gray-300 max-w-[100px] truncate" data-id="element-114">
                        {file.name}
                      </span>
                      <button onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500" data-id="element-115">
                        <X size={12} data-id="element-116" />
                      </button>
                    </div>)}
                </div>
              </div>}

            {/* Input Area */}
            <div className="p-3 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-700" data-id="element-117">
              {isRecording && <div className="mb-2 flex items-center gap-2 text-red-500 text-xs animate-pulse" data-id="element-118">
                  <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-119"></div>
                  {t.recording}
                </div>}
              <div className="flex items-center gap-2" data-id="element-120">
                {/* Voice Button */}
                <button onClick={isRecording ? stopVoiceRecording : startVoiceRecording} disabled={isLoading} className={`p-2 rounded-full transition-colors ${isRecording ? 'bg-red-500 text-white' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'} disabled:opacity-50`} title={t.voiceTooltip} data-id="element-121">
                  {isRecording ? <MicOff size={18} data-id="element-122" /> : <Mic size={18} data-id="element-123" />}
                </button>

                {/* File Upload Button */}
                <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={handleFileSelect} className="hidden" data-id="element-124" />
                <button onClick={() => fileInputRef.current?.click()} disabled={isLoading} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors disabled:opacity-50" title={t.fileTooltip} data-id="element-125">
                  <Paperclip size={18} data-id="element-126" />
                </button>

                {/* Camera Button */}
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraCapture} className="hidden" data-id="element-127" />
                <button onClick={() => cameraInputRef.current?.click()} disabled={isLoading} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors disabled:opacity-50" title={t.cameraTooltip} data-id="element-128">
                  <Camera size={18} data-id="element-129" />
                </button>

                {/* Text Input */}
                <div className="flex-1 flex items-center gap-2 bg-gray-100 dark:bg-slate-800 rounded-full px-4 py-2 border border-transparent focus-within:border-blue-500 transition-all" data-id="element-130">
                  <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyPress} placeholder={t.placeholder} className="flex-1 bg-transparent outline-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400" disabled={isLoading || isRecording} data-id="element-131" />
                  <button onClick={() => sendMessage(input)} disabled={!input.trim() && selectedFiles.length === 0 || isLoading} className="text-blue-600 disabled:text-gray-400 hover:scale-110 transition-transform disabled:hover:scale-100" data-id="element-132">
                    <Send size={18} data-id="element-133" />
                  </button>
                </div>
              </div>
              <div className="text-center mt-2" data-id="element-134">
                <p className="text-[10px] text-gray-400" data-id="element-135">{t.disclaimer}</p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
export default AIAssistant;